import React from 'react';
import { Wallet } from 'lucide-react';

// "O assinante também será o pagador do boleto?" (13/10/2026). Sim: o pagador é o
// assinante. Não: o vendedor informa quem paga — vai para a tabela `pagadores` e
// para a negociação, e toda UC dela nasce com esse pagador (migration
// 20261013200000). Usado no assistente de nova negociação e no "Editar cliente".

export interface DadosPerguntaPagador {
    /** null = ainda não respondido. */
    pagador_mesmo?: boolean | null;
    pagador_nome?: string;
    pagador_cpf_cnpj?: string;
    pagador_email?: string;
    pagador_whatsapp?: string;
}

const inputCls = 'w-full border border-gray-200 dark:border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all bg-white dark:bg-slate-900 text-gray-800 dark:text-slate-100 placeholder:text-gray-400 dark:placeholder:text-slate-500';
const labelCls = 'block text-xs font-bold text-gray-700 dark:text-slate-300 uppercase mb-1';

const mascaraDoc = (v: string) => {
    const d = v.replace(/\D/g, '').slice(0, 14);
    if (d.length <= 11) {
        return d.replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d{1,2})$/, '$1-$2');
    }
    return d.replace(/^(\d{2})(\d)/, '$1.$2').replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
        .replace(/\.(\d{3})(\d)/, '.$1/$2').replace(/(\d{4})(\d)/, '$1-$2');
};
const mascaraFone = (v: string) => v.replace(/\D/g, '').replace(/^(\d{2})(\d)/g, '($1) $2').replace(/(\d)(\d{4})$/, '$1-$2').slice(0, 15);

const PerguntaPagador: React.FC<{
    dados: DadosPerguntaPagador;
    /** Contato Financeiro já digitado — sugere e-mail e WhatsApp do pagador. */
    sugestao?: { email?: string; whatsapp?: string };
    obrigatoria?: boolean;
    onChange: (campo: keyof DadosPerguntaPagador, valor: any) => void;
}> = ({ dados, sugestao, obrigatoria = true, onChange }) => {
    const escolher = (mesmo: boolean) => {
        onChange('pagador_mesmo', mesmo);
        if (!mesmo) {
            if (!dados.pagador_email && sugestao?.email) onChange('pagador_email', sugestao.email);
            if (!dados.pagador_whatsapp && sugestao?.whatsapp) onChange('pagador_whatsapp', sugestao.whatsapp);
        }
    };
    const opcao = (valor: boolean, rotulo: string) => {
        const ativo = dados.pagador_mesmo === valor;
        return (
            <button
                type="button"
                role="radio"
                aria-checked={ativo}
                onClick={() => escolher(valor)}
                className={`flex-1 rounded-xl border px-4 py-2.5 text-sm font-semibold transition-colors ${ativo
                    ? 'border-primary bg-primary/10 text-primary dark:text-primary-light'
                    : 'border-gray-200 dark:border-slate-700 text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-800'}`}
            >
                {rotulo}
            </button>
        );
    };

    return (
        <fieldset className="rounded-xl border border-gray-200 dark:border-slate-700 p-4 space-y-3">
            <legend className="px-1 text-sm font-bold text-gray-800 dark:text-slate-100 flex items-center gap-2">
                <Wallet size={15} className="text-primary dark:text-primary-light" />
                O assinante também será o pagador do boleto?{obrigatoria ? <span className="text-red-500">*</span> : null}
            </legend>
            <p className="text-xs text-gray-500 dark:text-slate-400 -mt-1">
                O boleto sai no nome de quem paga. Responda "Não" quando quem paga é outra pessoa ou empresa.
            </p>
            <div role="radiogroup" aria-label="O assinante também será o pagador do boleto?" className="flex gap-3">
                {opcao(true, 'Sim, o assinante paga')}
                {opcao(false, 'Não, outra pessoa paga')}
            </div>
            {dados.pagador_mesmo === false ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                        <label className={labelCls} htmlFor="pagador-nome">Nome do pagador *</label>
                        <input id="pagador-nome" className={inputCls} value={dados.pagador_nome || ''} onChange={e => onChange('pagador_nome', e.target.value)} placeholder="Nome ou razão social" />
                    </div>
                    <div>
                        <label className={labelCls} htmlFor="pagador-doc">CPF/CNPJ do pagador *</label>
                        <input id="pagador-doc" className={inputCls} inputMode="numeric" value={dados.pagador_cpf_cnpj || ''} onChange={e => onChange('pagador_cpf_cnpj', mascaraDoc(e.target.value))} placeholder="000.000.000-00" />
                    </div>
                    <div>
                        <label className={labelCls} htmlFor="pagador-email">E-mail do pagador</label>
                        <input id="pagador-email" type="email" className={inputCls} value={dados.pagador_email || ''} onChange={e => onChange('pagador_email', e.target.value)} placeholder="opcional" />
                    </div>
                    <div>
                        <label className={labelCls} htmlFor="pagador-whats">WhatsApp do pagador</label>
                        <input id="pagador-whats" className={inputCls} value={dados.pagador_whatsapp || ''} onChange={e => onChange('pagador_whatsapp', mascaraFone(e.target.value))} placeholder="opcional" />
                    </div>
                </div>
            ) : null}
        </fieldset>
    );
};

export default PerguntaPagador;
